import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Server, Plus, Loader2, PlugZap, Unplug, Trash2, CheckCircle2, XCircle, AlertTriangle,
  Wrench, ShieldAlert, Play, Eye, ChevronDown, ChevronRight, Info, FlaskConical,
} from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

const SERVER_STATE = {
  connected: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", Icon: CheckCircle2, label: "Connected" },
  disconnected: { cls: "bg-slate-700/40 text-slate-400 border-slate-600/40", Icon: XCircle, label: "Disconnected" },
  error: { cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", Icon: AlertTriangle, label: "Error" },
};
const TOOL_STATE = {
  active: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", label: "Active" },
  "schema-changed": { cls: "bg-amber-500/10 text-amber-300 border-amber-500/30", label: "Schema changed — review" },
  disconnected: { cls: "bg-slate-700/40 text-slate-400 border-slate-600/40", label: "Disconnected" },
  removed: { cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", label: "Removed by server" },
};
const CLASS_COLORS = {
  read: "bg-sky-500/15 text-sky-300", paid: "bg-amber-500/15 text-amber-300",
  write: "bg-orange-500/15 text-orange-300", publishing: "bg-rose-500/15 text-rose-300",
};

function ServerStatePill({ status }) {
  const s = SERVER_STATE[status] || SERVER_STATE.disconnected;
  return <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${s.cls}`} data-testid={`mcp-status-${status}`}><s.Icon className="w-3 h-3" /> {s.label}</span>;
}

function AddServerDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", url: "", transport: "streamable-http", auth_type: "none", token: "", headers: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim() || !f.url.trim()) { toast.error("Name and URL are required."); return; }
    setBusy(true);
    try {
      const body = { name: f.name.trim(), url: f.url.trim(), transport: f.transport, auth_type: f.auth_type };
      if (f.auth_type === "bearer") body.token = f.token.trim();
      if (f.auth_type === "custom_headers") { try { body.headers = JSON.parse(f.headers || "{}"); } catch { toast.error("Headers must be valid JSON."); setBusy(false); return; } }
      await api.post("/mcp/servers", body);
      toast.success("Server added — click Connect to handshake & discover tools.");
      setOpen(false); setF({ name: "", url: "", transport: "streamable-http", auth_type: "none", token: "", headers: "" }); onAdded();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="mcp-add-server"><Plus className="w-4 h-4 mr-1.5" /> Add MCP server</Button></DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle>Add MCP server</DialogTitle>
          <DialogDescription className="text-slate-500">Connect a real Model Context Protocol server (Streamable HTTP). Secrets are encrypted at rest and never shown again.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-300 text-sm mb-1 block">Name</Label><Input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mcp-server-name" /></div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Server URL</Label><Input className={inputCls} value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://your-mcp-server.example.com/mcp" data-testid="mcp-server-url" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300 text-sm mb-1 block">Transport</Label>
              <Select value={f.transport} onValueChange={(v) => setF({ ...f, transport: v })}>
                <SelectTrigger className={inputCls} data-testid="mcp-server-transport"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  <SelectItem value="streamable-http">Streamable HTTP</SelectItem>
                  <SelectItem value="http-sse">HTTP + SSE (legacy)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300 text-sm mb-1 block">Auth</Label>
              <Select value={f.auth_type} onValueChange={(v) => setF({ ...f, auth_type: v })}>
                <SelectTrigger className={inputCls} data-testid="mcp-server-auth"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="bearer">Bearer token</SelectItem>
                  <SelectItem value="custom_headers">Custom headers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.transport === "http-sse" && <p className="text-[11px] text-amber-300/80">Legacy transport accepted for compatibility; Streamable HTTP is recommended.</p>}
          {f.auth_type === "bearer" && <div><Label className="text-slate-300 text-sm mb-1 block">Bearer token</Label><Input type="password" className={inputCls} value={f.token} onChange={(e) => setF({ ...f, token: e.target.value })} data-testid="mcp-server-token" /></div>}
          {f.auth_type === "custom_headers" && <div><Label className="text-slate-300 text-sm mb-1 block">Headers (JSON)</Label><Textarea className={`${inputCls} font-mono text-xs`} value={f.headers} onChange={(e) => setF({ ...f, headers: e.target.value })} placeholder='{"X-API-Key":"…"}' data-testid="mcp-server-headers" /></div>}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="mcp-server-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add server"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnableDialog({ tool, channels, onDone }) {
  const [open, setOpen] = useState(false);
  const [cls, setCls] = useState("read");
  const [scope, setScope] = useState(tool.scope_channels || []);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setScope((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/mcp/tools/${tool.id}/enable`, { classification: cls, scope_channels: scope }); toast.success("Tool enabled."); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`mcp-enable-${tool.id}`}><PlugZap className="w-4 h-4 mr-1.5" /> Enable</Button></DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle>Enable {tool.namespaced}</DialogTitle>
          <DialogDescription className="text-slate-500">You set the action classification — the MCP server's own annotations are never trusted for permissions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-300 text-sm mb-1 block">Action classification</Label>
            <Select value={cls} onValueChange={setCls}>
              <SelectTrigger className={inputCls} data-testid="mcp-classification"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                <SelectItem value="read">read — runs directly</SelectItem>
                <SelectItem value="paid">paid-generation — needs plan approval</SelectItem>
                <SelectItem value="write">write — needs plan approval</SelectItem>
                <SelectItem value="publishing">publishing — needs plan approval</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1 block">Channel scope <span className="text-slate-600">(none = all channels)</span></Label>
            <div className="flex flex-wrap gap-1.5" data-testid="mcp-scope-picker">
              {channels.map((c) => (
                <button key={c.id} onClick={() => toggle(c.id)} data-testid={`mcp-scope-${c.id}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${scope.includes(c.id) ? "bg-violet-600/20 text-violet-200 border-violet-500/40" : "bg-slate-900 text-slate-400 border-slate-700"}`}>
                  {c.data.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="mcp-enable-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enable tool"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InspectDialog({ tool }) {
  return (
    <Dialog>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={`mcp-inspect-${tool.id}`}><Eye className="w-4 h-4 mr-1.5" /> Schema</Button></DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle>{tool.namespaced} — input schema</DialogTitle></DialogHeader>
        <pre className="text-xs bg-slate-950 border border-slate-800 rounded-lg p-3 overflow-auto max-h-80 text-slate-300" data-testid="mcp-schema-json">{JSON.stringify(tool.input_schema, null, 2)}</pre>
      </DialogContent>
    </Dialog>
  );
}

function InvokeDialog({ tool, channels, onDone }) {
  const [open, setOpen] = useState(false);
  const [args, setArgs] = useState("{}");
  const [channelId, setChannelId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    let parsed; try { parsed = JSON.parse(args || "{}"); } catch { toast.error("Arguments must be valid JSON."); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/mcp/invoke", { namespaced: tool.namespaced, arguments: parsed, channel_id: channelId || null });
      setResult(data); if (data.ok) toast.success("Invoked."); else toast.error(data.error || "Refused.");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid={`mcp-invoke-${tool.id}`}><Play className="w-4 h-4 mr-1.5" /> Invoke</Button></DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle>Invoke {tool.namespaced}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-300 text-sm mb-1 block">Arguments (JSON)</Label><Textarea className={`${inputCls} font-mono text-xs min-h-[80px]`} value={args} onChange={(e) => setArgs(e.target.value)} data-testid="mcp-invoke-args" /></div>
          {(tool.scope_channels || []).length > 0 && (
            <div><Label className="text-slate-300 text-sm mb-1 block">Channel (scoped tool)</Label>
              <Select value={channelId || "none"} onValueChange={(v) => setChannelId(v === "none" ? "" : v)}>
                <SelectTrigger className={inputCls} data-testid="mcp-invoke-channel"><SelectValue placeholder="Select channel" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  <SelectItem value="none">None</SelectItem>
                  {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.data.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={run} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="mcp-invoke-run">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run tool"}</Button>
          {result && (
            <div data-testid="mcp-invoke-result">
              {result.ok ? (
                <div className="mt-1">
                  <div className="flex items-center gap-1.5 text-[11px] text-amber-300 mb-1"><ShieldAlert className="w-3.5 h-3.5" /> External untrusted content — not executed as instructions</div>
                  <pre className="text-xs bg-slate-950 border border-slate-800 rounded-lg p-3 overflow-auto max-h-60 text-slate-300 whitespace-pre-wrap">{result.text || JSON.stringify(result.content, null, 2)}</pre>
                  <p className="text-[11px] text-slate-500 mt-1">{result.latency_ms}ms · classification {result.classification}</p>
                </div>
              ) : (
                <div className="mt-1 flex items-start gap-2 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                  <XCircle className="w-4 h-4 text-rose-300 mt-0.5 flex-none" /><p className="text-xs text-rose-200">{result.error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToolRow({ tool, channels, onChange }) {
  const st = TOOL_STATE[tool.state] || TOOL_STATE.active;
  const disable = async () => { try { await api.post(`/mcp/tools/${tool.id}/disable`); toast.success("Disabled."); onChange(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3" data-testid={`mcp-tool-${tool.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-mono text-slate-100">{tool.namespaced}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${st.cls}`} data-testid={`tool-state-${tool.state}`}>{st.label}</span>
            {tool.enabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">enabled</span>}
            {tool.classification && <span className={`text-[10px] px-1.5 py-0.5 rounded ${CLASS_COLORS[tool.classification] || "bg-slate-700 text-slate-300"}`}>{tool.classification}</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1">{tool.description || "(no description)"}</p>
          {(tool.scope_channels || []).length > 0 && <p className="text-[11px] text-slate-600 mt-0.5">Scope: {tool.scope_channels.length} channel(s)</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <InspectDialog tool={tool} />
          {tool.enabled && <InvokeDialog tool={tool} channels={channels} onDone={onChange} />}
          {tool.enabled
            ? <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800" onClick={disable} data-testid={`mcp-disable-${tool.id}`}><Unplug className="w-4 h-4 mr-1.5" /> Disable</Button>
            : tool.state !== "removed" && <EnableDialog tool={tool} channels={channels} onDone={onChange} />}
        </div>
      </div>
    </div>
  );
}

function ServerCard({ server, channels, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [tools, setTools] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadTools = useCallback(async () => {
    const { data } = await api.get(`/mcp/tools?server_id=${server.id}`); setTools(data);
  }, [server.id]);
  useEffect(() => { if (expanded) loadTools(); }, [expanded, loadTools, server.status]);

  const act = async (fn) => { setBusy(true); try { await fn(); onChange(); if (expanded) loadTools(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const connect = () => act(async () => { const { data } = await api.post(`/mcp/servers/${server.id}/connect`); toast.success(`Connected — ${data.tools_discovered} tool(s) discovered (${data.latency_ms}ms).`); });
  const disconnect = () => act(async () => { await api.post(`/mcp/servers/${server.id}/disconnect`); toast("Disconnected."); });
  const del = () => act(async () => { await api.delete(`/mcp/servers/${server.id}`); toast.success("Server deleted."); });

  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-4" data-testid={`mcp-server-${server.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Server className="w-4 h-4 text-violet-300" />
            <span className="text-sm font-semibold text-slate-100">{server.name}</span>
            <ServerStatePill status={server.status} />
            {server.is_fixture && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" data-testid="mcp-fixture-badge"><FlaskConical className="w-3 h-3" /> Test fixture — verification only</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1 font-mono break-all">{server.url}</p>
          <p className="text-xs text-slate-600 mt-0.5">
            {server.transport}{server.protocol_version ? ` · MCP ${server.protocol_version}` : ""}{server.latency_ms ? ` · ${server.latency_ms}ms` : ""}
            {server.auth_type !== "none" ? ` · auth: ${server.auth_type}` : ""}{server.key_masked ? ` (${server.key_masked})` : ""}
          </p>
          {server.last_connected_at && <p className="text-[11px] text-emerald-500/70 mt-0.5">Last connected {new Date(server.last_connected_at).toLocaleString()}</p>}
          {server.last_error && <p className="text-[11px] text-rose-400/80 mt-0.5 break-all" data-testid={`mcp-error-${server.id}`}>{server.last_error}</p>}
          <p className="text-[11px] text-slate-600 mt-0.5">{server.tool_count} tool(s) · {server.enabled_count} enabled</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {server.status === "connected"
            ? <Button size="sm" onClick={disconnect} disabled={busy} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={`mcp-disconnect-${server.id}`}><Unplug className="w-4 h-4 mr-1.5" /> Disconnect</Button>
            : <Button size="sm" onClick={connect} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid={`mcp-connect-${server.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1.5" /> Connect &amp; discover</>}</Button>}
          <Button size="sm" variant="ghost" onClick={del} disabled={busy} className="text-rose-400 hover:bg-slate-800" data-testid={`mcp-delete-${server.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      {server.tool_count > 0 && (
        <button onClick={() => setExpanded((v) => !v)} className="mt-3 flex items-center gap-1.5 text-xs text-violet-300 hover:underline" data-testid={`mcp-toggle-tools-${server.id}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} {expanded ? "Hide" : "Show"} discovered tools
        </button>
      )}
      {expanded && (
        <div className="mt-3 space-y-2" data-testid={`mcp-tools-${server.id}`}>
          {tools.length === 0 ? <p className="text-xs text-slate-600">No tools.</p> :
            tools.map((t) => <ToolRow key={t.id} tool={t} channels={channels} onChange={() => { loadTools(); onChange(); }} />)}
        </div>
      )}
    </div>
  );
}

export default function MCP() {
  const { channels } = useStudio();
  const [servers, setServers] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { const { data } = await api.get("/mcp/servers"); setServers(data); } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!servers) return <LoadingState label="Loading MCP servers…" />;

  return (
    <div className="animate-in-up">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1 flex items-center gap-1.5"><Server className="w-3.5 h-3.5" /> Workspace</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">MCP Connections</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">Connect real Model Context Protocol servers. Tools are discovered live and stay <span className="text-slate-200">disabled until you opt in</span>, set their action class, and scope them. Enabled tools appear in the Model &amp; Tool Registry for the Director.</p>
        </div>
        <AddServerDialog onAdded={load} />
      </div>

      <div className="mb-5 flex items-start gap-2 bg-sky-500/5 border border-sky-500/20 rounded-xl p-4" data-testid="mcp-how-to">
        <Info className="w-4 h-4 text-sky-300 mt-0.5 flex-none" />
        <p className="text-xs text-slate-400">
          <span className="text-slate-200 font-medium">Add a real server:</span> click “Add MCP server”, paste its Streamable HTTP URL (e.g. <span className="font-mono">https://host/mcp</span>), choose Bearer/Custom-header auth if required, then Connect. The bundled “Test fixture” is for verification only and can be deleted once you connect a real server.
        </p>
      </div>

      {servers.length === 0 ? (
        <EmptyState icon={Server} title="No MCP servers" description="Add your first Model Context Protocol server to discover and govern its tools." />
      ) : (
        <div className="space-y-4" data-testid="mcp-server-list">
          {servers.map((s) => <ServerCard key={s.id} server={s} channels={channels} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
